import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { PageHeader } from '@/components/common/PageHeader';
import { EmptyState } from '@/components/common/EmptyState';
import { useAuth } from '@/hooks/useAuth';
import { DashboardApi } from '@/api';
import { formatMoneyWithSymbol } from '@/lib/format';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { StatusBadge } from '@/components/ui/StatusBadge';

export default function Dashboard() {
  const { user } = useAuth();
  const [dateRange, setDateRange] = useState<{ from: string; to: string } | null>(null);
  const [currency, setCurrency] = useState<string | null>(null);
  const [customerType, setCustomerType] = useState<string | null>(null);

  const isAdmin = user?.role === 'admin';

  // Admin queries
  const pipelineQuery = useQuery({
    queryKey: ['pipeline', currency, dateRange],
    queryFn: () => DashboardApi.pipeline({ currency, ...(dateRange && dateRange) }),
    enabled: isAdmin,
  });

  const funnelQuery = useQuery({
    queryKey: ['funnel', currency, dateRange, customerType],
    queryFn: () => DashboardApi.funnel({ currency, customerType, ...(dateRange && dateRange) }),
    enabled: isAdmin,
  });

  const conversionRateQuery = useQuery({
    queryKey: ['conversionRate', currency, dateRange],
    queryFn: () => DashboardApi.conversionRate({ currency, ...(dateRange && dateRange) }),
    enabled: isAdmin,
  });

  const discountWatchQuery = useQuery({
    queryKey: ['discountWatch', currency, dateRange],
    queryFn: () => DashboardApi.discountWatch({ currency, ...(dateRange && dateRange) }),
    enabled: isAdmin,
  });

  // Manager queries
  const mySummaryQuery = useQuery({
    queryKey: ['mySummary'],
    queryFn: () => DashboardApi.mySummary(),
    enabled: !isAdmin,
  });

  if (isAdmin) {
    return (
      <div className="space-y-5">
        <PageHeader
          title="Dashboard"
          subtitle="Pipeline, approvals, and performance metrics"
        />

        {/* Filters */}
        <div className="card card-pad flex flex-wrap gap-4 items-end">
          <div>
            <label className="label">From Date</label>
            <input
              type="date"
              className="input mt-1"
              value={dateRange?.from ?? ''}
              onChange={(e) =>
                setDateRange((prev) => ({
                  from: e.target.value,
                  to: prev?.to ?? new Date().toISOString().split('T')[0],
                }))
              }
            />
          </div>
          <div>
            <label className="label">To Date</label>
            <input
              type="date"
              className="input mt-1"
              value={dateRange?.to ?? ''}
              onChange={(e) =>
                setDateRange((prev) => ({
                  from: prev?.from ?? '',
                  to: e.target.value,
                }))
              }
            />
          </div>
          <div>
            <label className="label">Currency</label>
            <select
              className="input mt-1"
              value={currency ?? ''}
              onChange={(e) => setCurrency(e.target.value || null)}
            >
              <option value="">All</option>
              <option value="INR">INR</option>
              <option value="USD">USD</option>
              <option value="EUR">EUR</option>
            </select>
          </div>
          <div>
            <label className="label">Customer Type</label>
            <select
              className="input mt-1"
              value={customerType ?? ''}
              onChange={(e) => setCustomerType(e.target.value || null)}
            >
              <option value="">All</option>
              <option value="domestic">Domestic</option>
              <option value="export">Export</option>
            </select>
          </div>
        </div>

        {/* Pipeline Value Cards */}
        {pipelineQuery.data && (
          <div>
            <h3 className="font-semibold text-base mb-3">Pipeline Value by Currency</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {pipelineQuery.data.map((item: any) => (
                <div key={item.currency} className="card card-pad">
                  <div className="text-xs text-muted font-medium">{item.currency}</div>
                  <div className="text-2xl font-semibold mt-2">{formatMoneyWithSymbol(item.value, item.currency)}</div>
                  <div className="text-xs text-muted mt-1">{item.count} PIs</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Conversion Funnel + Rate side by side */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {funnelQuery.data && funnelQuery.data.length > 0 && (
            <div className="card card-pad">
              <h3 className="font-semibold text-base mb-4">Conversion Funnel</h3>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart
                  data={funnelQuery.data}
                  margin={{ top: 10, right: 20, left: 0, bottom: 10 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="status" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#2563eb" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {conversionRateQuery.data && conversionRateQuery.data.length > 0 && (
            <div className="card card-pad">
              <h3 className="font-semibold text-base mb-3">Conversion Rate</h3>
              <table className="table-clean">
                <thead>
                  <tr>
                    <th>Status</th>
                    <th>Count</th>
                    <th>Rate %</th>
                  </tr>
                </thead>
                <tbody>
                  {conversionRateQuery.data.map((item: any) => (
                    <tr key={item.status}>
                      <td className="font-medium">{item.status}</td>
                      <td>{item.count}</td>
                      <td>{item.rate}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Discount Watch */}
        {discountWatchQuery.data && discountWatchQuery.data.length > 0 && (
          <div className="card card-pad">
            <h3 className="font-semibold text-base mb-3">High Discounts Watch</h3>
            <div className="overflow-x-auto">
              <table className="table-clean">
                <thead>
                  <tr>
                    <th>PI #</th>
                    <th>Customer</th>
                    <th>Discount %</th>
                    <th>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {discountWatchQuery.data.map((item: any) => (
                    <tr key={item.id}>
                      <td className="font-medium">{item.pi_number}</td>
                      <td>{item.customer_name}</td>
                      <td className="text-rdx">{item.discount_pct}%</td>
                      <td>{formatMoneyWithSymbol(item.discount_amount, item.currency)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Manager Dashboard
  return (
    <div className="space-y-5">
      <PageHeader
        title="My Dashboard"
        subtitle="Your summary by PI status"
      />

      {mySummaryQuery.isLoading && (
        <div className="text-center py-12 text-muted">Loading…</div>
      )}

      {mySummaryQuery.data && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {mySummaryQuery.data.map((item: any) => (
            <div key={item.status} className="card card-pad">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xs text-muted font-medium uppercase tracking-wide">
                    {item.status}
                  </div>
                  <div className="text-2xl font-semibold mt-2">{item.count}</div>
                </div>
                <StatusBadge status={item.status} />
              </div>
              <div className="text-sm text-muted mt-3">
                Value: {formatMoneyWithSymbol(item.value, item.currency)}
              </div>
            </div>
          ))}
        </div>
      )}

      {!mySummaryQuery.isLoading && (!mySummaryQuery.data || mySummaryQuery.data.length === 0) && (
        <EmptyState
          title="No PIs yet"
          body="Start creating proforma invoices to see your summary here."
        />
      )}
    </div>
  );
}
